import React, { useState, useEffect } from 'react';
import { Upload, File as FileIcon, Trash2, RefreshCw, Download } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const UploadPanel = () => {
    const { token } = useAuth();
    const [files, setFiles] = useState([]);
    const [storage, setStorage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (token) {
            fetchFiles();
        }
    }, [token]);

    const fetchFiles = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/api/uploads/file_from_workspace`);
            setFiles(response.data.uploads);
            setStorage(response.data.storage);
            setError(null);
        } catch (error) {
            console.error('Error fetching files:', error);
            setError("Failed to load files");
        } finally {
            setLoading(false);
        }
    };

    const downloadFile = async (filename) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/api/uploads/download_file_from_workspace?filename=${encodeURIComponent(filename)}`,
                { responseType: 'blob' }
            );

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
            setError("Failed to download file");
        }
    };


    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        setError(null);
        try {
            await axios.post(`${API_BASE_URL}/api/uploads`, formData);
            await fetchFiles();
        } catch (error) {
            console.error('Error uploading file:', error);
            const errMsg = error.response?.data?.detail || "Upload failed";
            setError(errMsg);
        } finally {
            setUploading(false);
            // Reset file input
            event.target.value = null;
        }
    };

    const deleteFile = async (uploadId) => {
        if (!window.confirm("Are you sure you want to delete this file?")) return;

        try {
            await axios.delete(`${API_BASE_URL}/api/uploads/${uploadId}`);
            await fetchFiles();
        } catch (error) {
            console.error('Error deleting file:', error);
            setError("Failed to delete file");
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        if (!bytes) return 'N/A';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            <div className="p-4 border-b border-gray-200 bg-white">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Upload size={20} />
                    Data Uploads
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                    Upload persistent data files for your notebooks.
                </p>

                {storage && (
                    <div className="mt-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>Storage used: {formatFileSize(storage.used_bytes)}</span>
                            <span>{Math.round(storage.percentage)}% of {formatFileSize(storage.limit_bytes)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full ${storage.percentage > 90 ? 'bg-red-500' : 'bg-blue-600'}`}
                                style={{ width: `${Math.min(100, storage.percentage)}%` }}
                            ></div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-4 border-b border-gray-200 bg-white">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span>
                        </p>
                        <p className="text-xs text-gray-500">CSV, JSON, etc.</p>
                    </div>
                    <input
                        type="file"
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploading}
                    />
                </label>
                {uploading && (
                    <div className="mt-2 text-sm text-blue-600 text-center animate-pulse">
                        Uploading...
                    </div>
                )}
                {error && (
                    <div className="mt-2 text-sm text-red-600 text-center">
                        {error}
                    </div>
                )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-medium text-gray-700">Uploaded Files</h3>
                    <button
                        onClick={fetchFiles}
                        className="p-1 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                        title="Refresh list"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>

                {loading ? (
                    <div className="text-center text-gray-500 py-4">Loading...</div>
                ) : files.length === 0 ? (
                    <div className="text-center text-gray-500 py-8 text-sm">
                        No files uploaded yet.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {files.map((file) => (
                            <div
                                key={file.id}
                                className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow group"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                                        <FileIcon size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate" title={file.filename}>
                                            {file.filename}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {formatFileSize(file.size)} • {new Date(file.created_at).toLocaleDateString()}
                                        </p>
                                        <button
                                            onClick={() => downloadFile(file.filename)}
                                            className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                                            title="Download file"
                                        >
                                            <Download size={16} />
                                        </button>
                                        <button
                                            onClick={() => deleteFile(file.id)}
                                            className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            title="Delete file"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadPanel;
